import { useMutation } from '@tanstack/react-query';
import { useRequiredAppSelector } from '@/hooks';
import { useAttachEntityTag } from './useAttachEntityTag';
import { useCreateLine } from './useLineMutations';

type EntityType = 'act' | 'fragment' | 'scene';

interface CreateLineAndAttachData {
    title: string;
    color: string;
    entityType: EntityType;
    entityId: number;
    /** Detached in the same attach call (e.g. displacing the draft line X). */
    removeTagIds?: number[];
}

export function useCreateLineAndAttach() {
    const projectId = useRequiredAppSelector('projectId');
    const createLineMutation = useCreateLine();
    const attachTag = useAttachEntityTag();

    return useMutation({
        mutationFn: async ({ title, color, entityType, entityId, removeTagIds }: CreateLineAndAttachData) => {
            const result = await createLineMutation.mutateAsync({
                project_id: projectId,
                title,
                color,
            });

            const newLineId = result.data.entity_tag.id;

            await attachTag.mutateAsync({
                entityType,
                entityId,
                addTags: [newLineId],
                removeTags: removeTagIds,
            });

            return result;
        },

        onError: error => {
            // biome-ignore lint/suspicious/noConsole: surfaces mutation failures during development
            console.error('Failed to create line and attach:', error);
        },
    });
}
